import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logger } from '@flote/app-core';
const lazyCaptureException = (error: unknown, hint?: Record<string, unknown>) => {
  import('@flote/sentry-browser/api').then(({ captureException }) => captureException(error, hint));
};

interface ApiError {
  status?: number;
  body?: { message?: string; error?: string };
  message?: string;
}

function getErrorMessage(error: unknown): string {
  if (!error) return 'An unexpected error occurred';

  const apiError = error as ApiError;

  // Handle API response errors
  if (apiError.body?.message) return apiError.body.message;
  if (apiError.body?.error) return apiError.body.error;
  if (apiError.message) return apiError.message;

  // Handle status-based messages
  if (apiError.status) {
    switch (apiError.status) {
      case 401:
        return 'Session expired. Please sign in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  return 'An unexpected error occurred';
}

function shouldShowErrorToast(error: unknown): boolean {
  const apiError = error as ApiError;

  // Don't show toast for 401 errors (handled by auth flow)
  if (apiError.status === 401) return false;

  // Don't show toast for cancelled requests
  if (apiError.message?.includes('aborted') || apiError.message?.includes('cancelled')) {
    return false;
  }

  return true;
}

/**
 * Hook that sets up global error handling for React Query mutations.
 * Displays toast notifications for API errors.
 */
export function useGlobalErrorHandler(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const mutationCache = queryClient.getMutationCache();

    const unsubscribe = mutationCache.subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        const error = event.mutation.state.error;

        if (shouldShowErrorToast(error)) {
          const message = getErrorMessage(error);
          const mutationKey = JSON.stringify(event.mutation.options.mutationKey);

          logger.warn('API mutation error', {
            error: message,
            mutationKey,
          });

          // Capture to Sentry with mutation context
          lazyCaptureException(error, {
            tags: {
              type: 'mutation_error',
              mutationKey,
            },
            extra: {
              message,
              mutationKey,
            },
          });

          toast.error(message, {
            duration: 5000,
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);
}
